import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";
import { asDay, de, shiftWeek, weekdayLabel, weekOf, type MealPlanEntry, type Recipe } from "@ainkauf/core";
import { addMealPlanEntry, allRecipes, deleteMealPlanEntry, mealPlanOfWeek } from "@/db/repository";
import { MealSheet } from "./MealSheet";

/**
 * Der Wochenplan: sieben Tage untereinander.
 *
 * Bewusst keine Spalten und kein Ziehen. Auf 390 Pixel Breite wäre ein Tag
 * 50 Pixel breit, und Ziehen mit dem Daumen trifft die falsche Spalte. Sieben
 * Zeilen untereinander liest man von oben nach unten, und Antippen trifft
 * immer.
 */
export function WeekBoard({ onPlanToList }: { onPlanToList: (days: readonly string[]) => void }) {
  const [tage, setTage] = useState<string[]>(() => weekOf(new Date()));
  const [offenerTag, setOffenerTag] = useState<string | null>(null);

  const eintraege = useLiveQuery(() => mealPlanOfWeek(tage), [tage.join(",")], [] as MealPlanEntry[]);
  const rezepte = useLiveQuery(() => allRecipes(), [], [] as Recipe[]);

  const heute = asDay(new Date());
  const geplant = eintraege.length;

  const nameVon = (eintrag: MealPlanEntry) =>
    eintrag.recipe_id === null
      ? (eintrag.note ?? "")
      : (rezepte.find((r) => r.id === eintrag.recipe_id)?.name ?? eintrag.note ?? "");

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => {
            setTage(shiftWeek(tage, -1));
          }}
          aria-label={de.week.previous}
          data-testid="week-back"
          className="inline-flex size-11 items-center justify-center rounded-full bg-card text-ink-2 shadow-[var(--shadow-card)]"
        >
          <ChevronLeft size={20} strokeWidth={1.5} aria-hidden />
        </button>

        <span className="num text-sm font-semibold text-ink-2" data-testid="week-range">
          {spanne(tage)}
        </span>

        <button
          type="button"
          onClick={() => {
            setTage(shiftWeek(tage, 1));
          }}
          aria-label={de.week.next}
          data-testid="week-forward"
          className="inline-flex size-11 items-center justify-center rounded-full bg-card text-ink-2 shadow-[var(--shadow-card)]"
        >
          <ChevronRight size={20} strokeWidth={1.5} aria-hidden />
        </button>
      </header>

      <ul className="flex flex-col gap-2">
        {tage.map((tag, i) => {
          const amTag = eintraege.filter((eintrag) => eintrag.day === tag);
          const istHeute = tag === heute;

          return (
            <li
              key={tag}
              className="auftritt karte kante flex flex-col"
              data-testid="week-day"
              data-day={tag}
              style={{
                ["--takt" as string]: `${String(Math.min(i * 30, 200))}ms`,
                ["--kante-farbe" as string]: istHeute ? "var(--accent)" : "var(--paper-3)",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setOffenerTag(tag);
                }}
                data-testid="week-add"
                data-day={tag}
                className="flex min-h-[56px] items-center gap-3 px-4 py-2 text-left"
              >
                <span className={`w-24 shrink-0 text-sm ${istHeute ? "font-bold text-accent" : "font-semibold text-ink-2"}`}>
                  {istHeute ? de.week.today : weekdayLabel(tag)}
                </span>
                <span className="min-w-0 flex-1 truncate text-ink-3">
                  {amTag.length === 0 ? de.week.nothingPlanned : ""}
                </span>
                <Plus size={18} strokeWidth={2} className="shrink-0 text-ink-3" aria-hidden />
              </button>

              {amTag.length > 0 && (
                <ul className="flex flex-col gap-1 px-3 pb-3">
                  {amTag.map((eintrag) => (
                    <li key={eintrag.id} className="flex items-center gap-2 rounded-[var(--radius-md)] bg-paper-2 pl-3">
                      <span className="min-w-0 flex-1 truncate py-2 font-medium" data-testid="meal-entry">
                        {nameVon(eintrag)}
                        {eintrag.servings !== null && (
                          <span className="num ml-2 text-sm text-ink-2">{de.week.servingsOf(eintrag.servings)}</span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() => void deleteMealPlanEntry(eintrag.id)}
                        aria-label={`${de.week.remove}: ${nameVon(eintrag)}`}
                        data-testid="meal-remove"
                        className="inline-flex size-11 shrink-0 items-center justify-center rounded-full text-ink-3 transition-transform active:scale-90"
                      >
                        <X size={16} strokeWidth={2} aria-hidden />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      {geplant === 0 ? (
        <p className="karte px-5 py-8 text-center text-ink-2">
          {de.week.empty}
          <span className="mt-1 block text-sm">{de.week.emptyHint}</span>
        </p>
      ) : (
        <button
          type="button"
          onClick={() => {
            onPlanToList(tage);
          }}
          data-testid="week-to-list"
          className="min-h-[52px] rounded-[var(--radius-xl)] bg-accent px-5 font-display text-lg font-extrabold text-white shadow-[var(--shadow-card)] transition-transform active:scale-[0.99]"
        >
          {de.week.toList}
        </button>
      )}

      {offenerTag !== null && (
        <MealSheet
          day={offenerTag}
          recipes={rezepte}
          onSave={async (inhalt) => {
            await addMealPlanEntry(offenerTag, inhalt);
            setOffenerTag(null);
          }}
          onCancel={() => {
            setOffenerTag(null);
          }}
        />
      )}
    </div>
  );
}

/** "14. bis 20. September" über der Woche. */
function spanne(tage: readonly string[]): string {
  const [erster] = tage;
  const letzter = tage.at(-1);
  if (erster === undefined || letzter === undefined) return "";

  const kurz = new Intl.DateTimeFormat("de-DE", { day: "numeric" });
  const lang = new Intl.DateTimeFormat("de-DE", { day: "numeric", month: "long" });
  return `${kurz.format(new Date(erster))}. bis ${lang.format(new Date(letzter))}`;
}
